<div class="container-fluid">

  <div class="d-flex align-items-center mb-3">
    <a class="btn btn-link btn-sm p-0 me-3" routerLink="/marketplace-payments/pending-approval">
      <i class="bi bi-arrow-left me-1"></i>Back to queue
    </a>
  </div>

  @if (loading) {
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  } @else if (loadError) {
    <div class="alert alert-danger">{{ loadError }}</div>
  } @else if (payment) {

    <!-- ─── Header card ────────────────────────────────────────── -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="d-flex align-items-start justify-content-between mb-3">
          <div>
            <h4 class="mb-1">Payment {{ payment.id.slice(0, 8) }}…</h4>
            <div class="text-muted small">Job {{ payment.jobId.slice(0, 8) }}… · Bid {{ payment.bidId.slice(0, 8) }}…</div>
          </div>
          <span [class]="statusBadgeClass(payment.status)">
            {{ statusLabel(payment.status) }}
          </span>
        </div>

        <!--
          Header labels branch on isPreCapture: pre-capture rows
          carry the ESTIMATE in their billable_acres / price_per_acre
          / total_charge_amount columns (locked at acceptance), so
          labeling them as "Total charge" / "Billable acres" reads
          like the farmer was already charged. Post-capture keeps the
          definite framing because the charge actually fired.
          (2026-06-11 stg.)
        -->
        <div class="row g-4">
          <div class="col-md-3">
            <div class="text-muted small">
              {{ isPreCapture ? 'Estimated total' : 'Total charge' }}
            </div>
            <div class="fs-4 fw-semibold">{{ formatMoney(payment.totalChargeAmount) }}</div>
          </div>
          <div class="col-md-3">
            <div class="text-muted small">
              {{ isPreCapture ? 'Estimated acres' : 'Billable acres' }}
            </div>
            <div class="fs-4 fw-semibold">{{ formatAcres(payment.billableAcres) }}</div>
          </div>
          <div class="col-md-3">
            <div class="text-muted small">Per-acre rate</div>
            <div class="fs-4 fw-semibold">{{ formatMoney(payment.pricePerAcre) }}</div>
          </div>
          <div class="col-md-3">
            <div class="text-muted small">Attempts</div>
            <div class="fs-4 fw-semibold">{{ payment.acceptanceAttemptCount }}</div>
          </div>
        </div>

        <!--
          Actuals-divergence callout: the payment row's billable_acres
          stays at the estimate until either farmer_accepted_actuals
          fires OR an admin_adjusted lands. During the dispute window
          (and the pre-acceptance pending_admin_approval window), the
          pilot's reported actuals live in the latest actuals_reported
          revision. Surface the new acres + recomputed total here so
          the admin can see "estimate was X, pilot reported Y" at a
          glance, without having to scan the revision timeline below.
          (2026-06-11 — caught when admin dispute UX showed the
          estimate without surfacing the actual.)
        -->
        @if (hasActualsDivergence && latestReportedActuals; as reported) {
          <div class="mt-3 p-2 rounded small"
               [class.bg-warning-subtle]="latestReportedActuals!.billableAcres! > (payment.billableAcres ?? 0)"
               [class.bg-info-subtle]="latestReportedActuals!.billableAcres! < (payment.billableAcres ?? 0)">
            <i class="bi bi-clipboard-check me-2"></i>
            <strong>Pilot reported {{ formatAcres(reported.billableAcres) }}</strong>
            (estimate was {{ formatAcres(payment.billableAcres) }}) —
            recomputes to {{ formatMoney(reported.totalChargeAmount) }} total
            ({{ formatMoney((reported.totalChargeAmount ?? 0) - payment.totalChargeAmount) }} change)
          </div>
        }

        @if (payment.originalAgreedAmount != null && payment.originalAgreedAmount !== payment.totalChargeAmount) {
          <!--
            bg-body-tertiary auto-adapts to the active data-bs-theme.
            Was bg-light — hard-coded white made the text unreadable
            against the dark surface in dark mode. (Stg 2026-06-11.)
          -->
          <div class="mt-3 p-2 bg-body-tertiary rounded small">
            <i class="bi bi-info-circle me-2"></i>
            Original estimate: {{ formatMoney(payment.originalAgreedAmount) }} →
            Current total: {{ formatMoney(payment.totalChargeAmount) }}
            ({{ formatMoney(payment.totalChargeAmount - payment.originalAgreedAmount) }} change)
          </div>
        }

        @if (payment.failureReason) {
          <div class="mt-3 alert alert-danger mb-0 py-2">
            <strong>Failure reason:</strong> {{ payment.failureReason }}
          </div>
        }
      </div>
    </div>

    <!-- ─── Fee breakdown card ────────────────────────────────── -->
    <div class="card mb-3">
      <div class="card-body">
        <h6 class="mb-3 text-muted">Fee breakdown</h6>
        <div class="row g-3 small">
          <div class="col-md-4">
            <div class="d-flex justify-content-between">
              <span>Gross amount</span><span>{{ formatMoney(payment.grossAmount) }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <span>Farmer surcharge (5%)</span><span>{{ formatMoney(payment.farmerSurchargeAmount) }}</span>
            </div>
            <div class="d-flex justify-content-between fw-semibold border-top pt-1 mt-1">
              <span>Farmer total</span><span>{{ formatMoney(payment.totalChargeAmount) }}</span>
            </div>
          </div>
          <div class="col-md-4">
            <div class="d-flex justify-content-between">
              <span>Pilot deduction (5%)</span><span>{{ formatMoney(payment.pilotFeeDeductionAmount) }}</span>
            </div>
            <div class="d-flex justify-content-between fw-semibold border-top pt-1 mt-1">
              <span>Pilot payout</span><span>{{ formatMoney(payment.pilotPayoutAmount) }}</span>
            </div>
          </div>
          <div class="col-md-4">
            <div class="d-flex justify-content-between">
              <span>Captured at</span><span>{{ formatDateTime(payment.capturedAt) }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <span>Payout eligible</span><span>{{ formatDateTime(payment.payoutEligibleAt) }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <span>Transferred at</span><span>{{ formatDateTime(payment.transferredAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Revision timeline ─────────────────────────────────── -->
    <div class="card mb-3">
      <div class="card-body">
        <h6 class="mb-3 text-muted">Revision timeline</h6>
        @if (revisions.length === 0) {
          <div class="text-muted small fst-italic">No revisions recorded for this payment yet.</div>
        } @else {
          <div class="timeline position-relative ps-4">
            @for (rev of revisions; track rev.id) {
              <div class="position-relative pb-3" [class]="'timeline-item ' + revisionToneClass(rev.revisionType)">
                <div class="d-flex justify-content-between align-items-baseline">
                  <strong>{{ revisionLabel(rev.revisionType) }}</strong>
                  <span class="text-muted small">{{ formatDateTime(rev.createdAt) }}</span>
                </div>
                <div class="text-muted small mb-1">{{ formatActor(rev.actorType, rev.actorProfileId) }}</div>
                @if (rev.billableAcres != null && rev.totalChargeAmount != null) {
                  <!--
                    Show the full math: acres × rate = GROSS, then +5%
                    farmer surcharge = TOTAL. Without surfacing the
                    surcharge step, the reader sees "33.31 × $15 =
                    $524.63" which doesn't add up (33.31 × $15 = $499.65,
                    not $524.63). The total INCLUDES the 5% surcharge
                    so the math has to show both steps. (Stg 2026-06-11
                    — Chris caught the missing-surcharge math.)
                  -->
                  <div class="small">
                    {{ formatAcres(rev.billableAcres) }} × {{ formatMoney(rev.pricePerAcre) }}/ac
                    = {{ formatMoney(rev.grossAmount) }} gross
                    @if (rev.farmerSurchargeAmount != null) {
                      + {{ formatMoney(rev.farmerSurchargeAmount) }} fee (5%)
                    }
                    = {{ formatMoney(rev.totalChargeAmount) }} total
                  </div>
                }
                @if (rev.notes) {
                  <div class="small fst-italic text-muted mt-1">"{{ rev.notes }}"</div>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>

    <!-- ─── Action footer ─────────────────────────────────────── -->
    <div class="card mb-3 sticky-bottom" style="bottom: 0;">
      <div class="card-body d-flex flex-wrap gap-2 justify-content-end">
        @if (canApprove) {
          <button type="button" class="btn btn-outline-danger" (click)="showDecline = true">
            <i class="bi bi-x-circle me-1"></i>Decline
          </button>
          <button type="button" class="btn btn-outline-secondary" (click)="showAdjust = true">
            <i class="bi bi-pencil me-1"></i>Adjust
          </button>
          <button type="button" class="btn btn-success" (click)="showApprove = true">
            <i class="bi bi-check-circle me-1"></i>Approve &amp; capture
          </button>
        }
        @if (canEarlyRelease) {
          <button type="button" class="btn btn-warning" (click)="showEarlyRelease = true">
            <i class="bi bi-fast-forward-fill me-1"></i>Release payout early
          </button>
        }
      </div>
    </div>

    <!-- ─── Modals ────────────────────────────────────────────── -->

    @if (showApprove) {
      <app-confirmation-modal
        title="Approve &amp; capture this payment?"
        [message]="'This will charge the farmer ' + formatMoney(payment.totalChargeAmount) + ' and schedule the pilot payout. Proceed?'"
        confirmLabel="Approve &amp; capture"
        variant="primary"
        [processing]="processing"
        processingLabel="Capturing..."
        (confirmed)="onApproveConfirmed()"
        (cancelled)="showApprove = false">
      </app-confirmation-modal>
    }

    @if (showAdjust) {
      <app-adjust-modal
        [currentBillableAcres]="payment.billableAcres ?? 0"
        [currentPricePerAcre]="payment.pricePerAcre ?? 0"
        [currentTotalCharge]="payment.totalChargeAmount"
        [processing]="processing"
        (confirmed)="onAdjustConfirmed($event)"
        (cancelled)="showAdjust = false">
      </app-adjust-modal>
    }

    @if (showDecline) {
      <app-confirmation-modal-with-reason
        title="Decline this payment?"
        message="The pilot will NOT be paid for this row. Please record why you're declining."
        confirmLabel="Decline"
        variant="danger"
        icon="bi-x-circle"
        reasonLabel="Decline reason"
        reasonPlaceholder="e.g. Job not delivered, refund needed via support"
        [reasonRequired]="true"
        [processing]="processing"
        (confirmed)="onDeclineConfirmed($event)"
        (cancelled)="showDecline = false">
      </app-confirmation-modal-with-reason>
    }

    @if (showEarlyRelease) {
      <app-confirmation-modal-with-reason
        title="Release payout early?"
        message="This will skip the remaining payout window and transfer funds to the pilot immediately. Reason is required for audit."
        confirmLabel="Release now"
        variant="warning"
        icon="bi-fast-forward-fill"
        reasonLabel="Early-release reason"
        reasonPlaceholder="e.g. Pilot urgent cash-flow need, approved per ops call"
        [reasonRequired]="true"
        [processing]="processing"
        (confirmed)="onEarlyReleaseConfirmed($event)"
        (cancelled)="showEarlyRelease = false">
      </app-confirmation-modal-with-reason>
    }

  }
</div>
